// iter58 · Slice 1C — Job History drawer.
// Renders the Estimate → Proposal → Contract → Invoice version chain
// tree returned by GET /foundation/jobs/{jid}/history plus an Unlinked
// bucket. Uses shadcn <Sheet> per user pick. Contractor-friendly labels
// only; no raw event keys or DB terms.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { FileText, ChevronRight, Lock, CircleDot, AlertCircle } from "lucide-react";

// ---------------------------------------------------------------------
// Universal status → contractor-friendly label. Matches Slice 1B's
// approved vocabulary. Keep the map in sync with JobDetail.jsx.
// ---------------------------------------------------------------------
const STATUS_LABEL = {
  draft: "Draft", ready_to_send: "Ready to send",
  sent: "Sent", delivered: "Delivered", viewed: "Viewed",
  action_required: "Action required", revision_requested: "Revision requested",
  approved: "Approved",
  awaiting_signature: "Waiting for Signature",
  partially_signed: "Partially signed", signed: "Signed",
  fully_executed: "Fully Executed",
  declined: "Declined", expired: "Expired",
  replaced: "Replaced", canceled: "Canceled",
  uploaded: "Uploaded", shared: "Shared",
  payment_due: "Payment Due", partially_paid: "Partially Paid", paid: "Paid",
};

const ROUTE_BY_TYPE = {
  estimate: "/app/estimates/", proposal: "/app/proposals/",
  contract: "/app/contracts/", invoice: "/app/invoices/",
};

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

// One card for any leaf document — used by every layer.
const DocCard = ({ card, indent = 0 }) => {
  const openHref = card.id && ROUTE_BY_TYPE[card.doc_type]
    ? `${ROUTE_BY_TYPE[card.doc_type]}${card.id}` : null;
  const title = card.doc_type === "estimate"
    ? `Estimate v${card.version || 1}`
    : card.doc_type === "invoice"
      ? `Invoice ${card.doc_number ? `#${card.doc_number}` : ""}`.trim()
      : `${card.doc_type.charAt(0).toUpperCase()}${card.doc_type.slice(1)}`
        + (card.version && card.version > 1 ? ` v${card.version}` : "");
  return (
    <div
      data-testid={`history-card-${card.doc_type}-${card.id}`}
      style={{ marginLeft: `${indent * 14}px` }}
      className={`jc-card p-2.5 border-l-4 ${card.is_current ? "border-l-green-500 bg-green-50/40" : card.is_locked ? "border-l-slate-300 bg-slate-50/50" : "border-l-[var(--jc-navy)]/40"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {indent > 0 && <ChevronRight size={12} className="text-[var(--jc-text-soft)] shrink-0" />}
          <FileText size={12} className="text-[var(--jc-text-soft)] shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{title}</div>
            <div className="text-[11px] text-[var(--jc-text-soft)]">
              {STATUS_LABEL[card.status] || card.status}
              {" · "}
              {(card.visibility || "internal_only") === "internal_only" ? "Internal only" : "Customer visible"}
              {card.last_activity_at && <> · last activity {formatWhen(card.last_activity_at)}</>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {card.is_current && (
            <span data-testid={`history-current-badge-${card.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-600 text-white">
              <CircleDot size={10} /> Current
            </span>
          )}
          {card.is_locked && !card.is_current && (
            <span data-testid={`history-locked-badge-${card.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 text-slate-700">
              <Lock size={10} /> Locked
            </span>
          )}
          {openHref && (
            <Link
              to={openHref}
              data-testid={`history-open-${card.id}`}
              className="text-xs font-semibold underline text-[var(--jc-navy)]"
            >Open</Link>
          )}
        </div>
      </div>
    </div>
  );
};

const ChainNode = ({ estimate }) => {
  return (
    <div className="space-y-1.5" data-testid={`history-chain-${estimate.id}`}>
      <DocCard card={estimate} indent={0} />
      {(estimate.proposals || []).length === 0 && (
        <div className="ml-6 text-[11px] text-[var(--jc-text-soft)] italic" data-testid={`history-no-proposal-${estimate.id}`}>
          No Proposal created yet
        </div>
      )}
      {(estimate.proposals || []).map((p) => (
        <div key={p.id} className="space-y-1.5">
          <DocCard card={p} indent={1} />
          {(p.contracts || []).length === 0 && (p.invoices || []).length === 0 && (
            <div className="ml-12 text-[11px] text-[var(--jc-text-soft)] italic" data-testid={`history-no-contract-${p.id}`}>
              No Contract yet
            </div>
          )}
          {(p.contracts || []).map((c) => (
            <div key={c.id} className="space-y-1.5">
              <DocCard card={c} indent={2} />
              {(c.invoices || []).length === 0 && (
                <div className="ml-16 text-[11px] text-[var(--jc-text-soft)] italic" data-testid={`history-no-invoice-${c.id}`}>
                  No Invoice yet
                </div>
              )}
              {(c.invoices || []).map((i) => (
                <DocCard key={i.id} card={i} indent={3} />
              ))}
            </div>
          ))}
          {/* Invoices bound directly to the proposal (no contract). */}
          {(p.invoices || []).map((i) => (
            <DocCard key={i.id} card={i} indent={2} />
          ))}
        </div>
      ))}
      {/* Invoices bound directly to the estimate (no proposal). */}
      {(estimate.invoices || []).map((i) => (
        <DocCard key={i.id} card={i} indent={1} />
      ))}
    </div>
  );
};

const UNLINKED_REASON_LABEL = {
  no_source_reference: "This document belongs to the Job, but its original source relationship could not be confirmed.",
  raw_record_missing: "This document's underlying record could not be resolved.",
  non_chain_document: "This document is not part of the Estimate → Invoice chain.",
};

export const JobHistoryDrawer = ({ open, onOpenChange, jobId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return;
    setLoading(true);
    setErr(null);
    api.get(`/foundation/jobs/${jobId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Could not load Job History"))
      .finally(() => setLoading(false));
  }, [open, jobId]);

  const chains = data?.chains || [];
  const unlinked = data?.unlinked || [];
  const empty = !loading && chains.length === 0 && unlinked.length === 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="job-history-drawer"
        className="w-full sm:max-w-2xl overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle data-testid="job-history-title">Job History</SheetTitle>
          <SheetDescription>
            Every Estimate, Proposal, Contract, and Invoice tied to this Job.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {loading && (
            <div className="text-sm text-[var(--jc-text-soft)]" data-testid="job-history-loading">
              Loading history…
            </div>
          )}
          {err && (
            <div className="text-sm text-red-700 flex items-center gap-1" data-testid="job-history-error">
              <AlertCircle size={14} /> {err}
            </div>
          )}
          {empty && (
            <div className="jc-card p-4 text-sm text-[var(--jc-text-soft)]" data-testid="job-history-empty">
              <div className="font-semibold text-[var(--jc-text)]">No documents yet</div>
              <div className="mt-1">Create an Estimate to start this Job&apos;s document history.</div>
            </div>
          )}

          {chains.map((ch) => (
            <div key={ch.root_estimate_id} className="space-y-2" data-testid={`history-root-${ch.root_estimate_id}`}>
              {(ch.estimates || []).map((e) => (
                <ChainNode key={e.id} estimate={e} />
              ))}
            </div>
          ))}

          {unlinked.length > 0 && (
            <div className="pt-2 border-t border-[var(--jc-border)]" data-testid="history-unlinked-group">
              <div className="text-xs font-semibold text-[var(--jc-text-soft)] mb-2">
                Unlinked documents
              </div>
              <div className="space-y-1.5">
                {unlinked.map((u, ix) => (
                  <div key={u.meta_id || u.id || ix} className="jc-card p-2.5 border-l-4 border-l-amber-400 bg-amber-50/30" data-testid={`history-unlinked-${u.meta_id || u.id || ix}`}>
                    <div className="text-sm font-semibold capitalize">
                      {u.doc_type?.replace(/_/g, " ") || "Document"}
                      {u.doc_number && <span> · #{u.doc_number}</span>}
                    </div>
                    <div className="text-[11px] text-[var(--jc-text-soft)]">
                      {STATUS_LABEL[u.status] || u.status}
                      {" · "}
                      {(u.visibility || "internal_only") === "internal_only" ? "Internal only" : "Customer visible"}
                    </div>
                    <div className="text-[11px] text-amber-800 mt-1">
                      {UNLINKED_REASON_LABEL[u.reason] || "This document could not be attached to a chain."}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default JobHistoryDrawer;
